import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ClipboardCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';

/* Nachtragen (29.09.2026): Was die Abschlussquote verfälscht, bis es jemand
   klärt — Verträge ohne Angabe zum Probetraining, vergangene Probetrainings
   ohne Status, Erschienene ohne Ergebnis. Jede Zeile ist mit einem Klick erledigt. */
const tag = (iso) => (iso || '').slice(0, 10).split('-').reverse().join('.');
const knopf = 'rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50';

export default function SalesNachtragen({ nachtragen, onGeaendert }) {
  const [busy, setBusy] = useState('');
  const [datum, setDatum] = useState({});
  if (!nachtragen) return null;
  const { vertraege = [], termine_offen = [], ohne_ergebnis = [] } = nachtragen;
  if (!vertraege.length && !termine_offen.length && !ohne_ergebnis.length) return null;

  const los = async (schluessel, aufruf, ok) => {
    setBusy(schluessel);
    try { await aufruf(); toast.success(ok); onGeaendert?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
    setBusy('');
  };
  const angabe = (v, body, ok) => los(v.vertrag_id, () => api.post(`/vertraege/${v.vertrag_id}/probetraining`, body), ok);
  const status = (t, erschienen) => los(t.buchung_id || t.lead_id, () => (t.art === 'buchung'
    ? api.post(`/probetrainings/${t.buchung_id}/status`, null, { params: { status: erschienen ? 'erschienen' : 'nicht_erschienen' } })
    : api.put(`/leads/${t.lead_id}`, { termin_status: erschienen ? 'besucht' : 'no_show' })), erschienen ? 'Erschienen eingetragen' : 'No-Show eingetragen');

  return (
    <Card className="border-amber-300" data-testid="st-nachtragen"><CardContent className="p-4 space-y-4">
      <div>
        <h2 className="text-sm font-bold flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-amber-600" /> Nachtragen</h2>
        <p className="text-xs text-muted-foreground">Bis das geklärt ist, fehlen diese Fälle in der Abschlussquote oder drücken sie.</p>
      </div>

      {vertraege.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Verträge: gab es vorher ein Probetraining? ({vertraege.length})</p>
          {vertraege.map(v => (
            <div key={v.vertrag_id} className="rounded-lg border border-border/60 p-2.5 space-y-1.5" data-testid="st-nachtrag-vertrag">
              <div className="text-sm"><b>{v.name}</b> <span className="text-xs text-muted-foreground">· Vertrag {tag(v.am)}{v.tarif ? ` · ${v.tarif}` : ''}{v.berater ? ` · ${v.berater}` : ''}</span></div>
              <div className="flex flex-wrap items-center gap-1.5">
                {v.kandidaten.filter(k => k.status !== 'nicht_erschienen').map(k => (
                  <button key={k.buchung_id || k.lead_id} className={knopf} disabled={busy === v.vertrag_id}
                    onClick={() => angabe(v, k.art === 'buchung' ? { art: 'buchung', buchung_id: k.buchung_id } : { art: 'lead', lead_id: k.lead_id, am: k.am }, 'Probetraining zugeordnet')}>
                    Ja: {k.name} am {tag(k.am)}
                  </button>
                ))}
                <input type="date" value={datum[v.vertrag_id] || ''} max={v.am} onChange={e => setDatum(d => ({ ...d, [v.vertrag_id]: e.target.value }))}
                  className="h-7 px-2 border border-border rounded-md text-xs bg-background" title="Probetraining an einem anderen Tag" />
                <button className={knopf} disabled={!datum[v.vertrag_id] || busy === v.vertrag_id}
                  onClick={() => angabe(v, { art: 'datum', am: datum[v.vertrag_id] }, 'Probetraining eingetragen')}>Ja, an diesem Tag</button>
                <button className={knopf} disabled={busy === v.vertrag_id} onClick={() => angabe(v, { art: 'ohne' }, 'Als Abschluss ohne Probetraining vermerkt')}>Nein, direkt abgeschlossen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {termine_offen.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Probetrainings vorbei, Status fehlt ({termine_offen.length})</p>
          {termine_offen.map(t => (
            <div key={t.buchung_id || t.lead_id} className="flex flex-wrap items-center gap-2 text-sm" data-testid="st-nachtrag-termin">
              <span className="min-w-40"><b>{t.name}</b> <span className="text-xs text-muted-foreground">{tag(t.am)}</span></span>
              <button className={knopf} disabled={busy === (t.buchung_id || t.lead_id)} onClick={() => status(t, true)}>Erschienen</button>
              <button className={knopf} disabled={busy === (t.buchung_id || t.lead_id)} onClick={() => status(t, false)}>Nicht erschienen</button>
              {t.art === 'buchung' && (
                <button className={knopf} disabled={busy === t.buchung_id} title="Hat einen neuen Termin — der alte zählt nicht mehr"
                  onClick={() => los(t.buchung_id, () => api.post(`/probetrainings/${t.buchung_id}/absagen`, { grund: 'verschoben' }), 'Als verschoben ausgetragen')}>Neu terminiert</button>
              )}
            </div>
          ))}
        </div>
      )}

      {ohne_ergebnis.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Erschienen, Ergebnis fehlt ({ohne_ergebnis.length})</p>
          {ohne_ergebnis.map(b => (
            <div key={b.buchung_id || b.lead_id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="min-w-40"><b>{b.name}</b> <span className="text-xs text-muted-foreground">{tag(b.am)}</span></span>
              <button className={knopf} disabled={busy === (b.buchung_id || b.lead_id)}
                onClick={() => los(b.buchung_id || b.lead_id, () => api.post(b.art === 'lead' ? `/probetrainings/lead/${b.lead_id}/ergebnis` : `/probetrainings/${b.buchung_id}/ergebnis`, { ergebnis: 'bedenkzeit' }), 'Bedenkzeit eingetragen')}>Bedenkzeit</button>
              <Link to="/probetrainings" className="text-xs underline text-muted-foreground">Kein Interesse (mit Grund) unter Probetrainings</Link>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">Kam ein Vertrag zustande, oben beim Vertrag „Ja: …“ wählen.</p>
        </div>
      )}
    </CardContent></Card>
  );
}
